import { useEffect, useState } from 'react'
import useAuth from './useAuth'
import useAppSync from './useAppSync'
import { V6Client, GraphqlSubscriptionMessage } from '@aws-amplify/api-graphql'
import { Subscription } from 'rxjs'
import { ProjectMembershipRole, UserRole } from '../enums'
import { subscribe } from '../graphql/subscriptions'
import { SubscribeSubscription } from '../API'
import { getAccessToken, handleRefreshSession } from '../utils/cognito'

export interface Subscribe {
  next?: (value: GraphqlSubscriptionMessage<SubscribeSubscription>) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error?: (error: any) => void
}

type NextHandler = (value: GraphqlSubscriptionMessage<SubscribeSubscription>) => void

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ErrorHandler = (error: any) => void

export default function useSubscribe(handleNext: NextHandler, onError?: ErrorHandler) {
  /**
   * The auth state.
   */
  const { currentUser } = useAuth()

  /**
   * The auth token.
   */
  const authToken = getAccessToken()

  /**
   * Refresh the client.
   */
  const refreshClient = async () => {
    // We should check here the auth provider, but since the AppSync client can only be used with Cognito,
    // we can safely assume that the auth provider is Cognito.
    await handleRefreshSession()

    const newClient = useAppSync()
    setClient(newClient)
  }

  /**
   * Get the initial client.
   */
  const getInitialClient = () => {
    return useAppSync()
  }

  /**
   * The AWS AppSync client.
   */
  const [client, setClient] = useState<V6Client>(getInitialClient())

  /**
   * Get the channels to subscribe.
   */
  const getChannelNames = () => {
    let channels: string[] = []

    if (currentUser && currentUser.notificationsInApp) {
      channels.push(`user:${currentUser.id}`)

      if (
        currentUser.companyId &&
        currentUser.showInAppNotificationsFromCompany &&
        (currentUser.role === UserRole.SUPERADMIN || currentUser.role === UserRole.ADMIN)
      ) {
        channels.push(`company:${currentUser.companyId}`)
      }

      channels = channels.concat(
        (currentUser?.projects ?? [])
          .filter(
            (membership) =>
              membership.showInAppNotifications &&
              (membership.role === ProjectMembershipRole.ADMIN ||
                membership.role === ProjectMembershipRole.OWNER)
          )
          .map((membership) => `project:${membership.projectId}`)
      )
    }

    return channels
  }

  /**
   * Handle the error message.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleError = (error: any) => {
    const hasErrors = error?.errors && Array.isArray(error.errors) && error.errors.length > 0

    if (hasErrors) {
      const isUnauthorized = error.errors.some(
        (e: { errorType: string }) => e?.errorType === 'UnauthorizedException'
      )

      if (isUnauthorized) {
        refreshClient()
      }
    }

    onError?.(error)
  }

  useEffect(() => {
    // The subscriptions.
    const subscriptions: Subscription[] = []

    // Get the channel names.
    const names = getChannelNames()

    // Subscribe to the channels.
    names.forEach((name) => {
      subscriptions.push(
        client
          .graphql({
            query: subscribe,
            variables: { name }
          })
          .subscribe({
            next: handleNext,
            error: handleError
          })
      )
    })

    return () => subscriptions.forEach((subscription) => subscription.unsubscribe())
  }, [currentUser, client])

  /**
   * Auto-refresh the client every five minutes to keep the authentication alive.
   */
  useEffect(() => {
    const FIVE_MINUTES = 1000 * 60 * 5
    const interval = setInterval(() => {
      refreshClient()
    }, FIVE_MINUTES)
    return () => clearInterval(interval)
  }, [authToken])
}
